'use client';

import { Dialog, DialogContent, type DialogProps } from '@radix-ui/react-dialog';
import { wrapClassNames } from '@ui/utils/tailwind';
import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className = '', ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={wrapClassNames(
      `flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground
      ${className}`
    )}
    {...props}
  />
));

const CommandDialog = ({ children, ...props }: DialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0">
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className = '', ...props }, ref) => (
  <div className="mt-1">
    <CommandPrimitive.Input
      cmdk-input-wrapper=""
      ref={ref}
      className={wrapClassNames(
        `w-full !rounded-lg h-9
        p-3 text-black font-normal !text-sm leading-5 border-none focus:outline-none focus:ring-0
        ${className}`
      )}
      {...props}
    />
  </div>
));

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className = '', ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={wrapClassNames(`max-h-[300px] overflow-y-auto overflow-x-hidden ${className}`)}
    {...props}
  />
));

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => <CommandPrimitive.Empty ref={ref} className="py-6 text-center text-sm" {...props} />);

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className = '', ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={wrapClassNames(
      `overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground
      ${className}`
    )}
    {...props}
  />
));

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className = '', ...props }, ref) => (
  <CommandPrimitive.Separator ref={ref} className={wrapClassNames(`-mx-1 h-px bg-border ${className}`)} {...props} />
));

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className = '', ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={wrapClassNames(
      `relative flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0
      ${className}`
    )}
    {...props}
  />
));

const CommandShortcut = ({ className = '', ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span className={wrapClassNames(`ml-auto text-xs tracking-widest text-muted-foreground ${className}`)} {...props} />
  );
};
CommandShortcut.displayName = 'CommandShortcut';

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
